import React, { useState, KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface MultiEmailInputProps {
  emails: string[];
  onEmailsChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  emails,
  onEmailsChange,
  placeholder = "Enter email addresses...",
  className = "",
}) => {
  const [inputValue, setInputValue] = useState("");

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      validateEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      onEmailsChange([...emails, trimmedEmail]);
      setInputValue("");
    }
  };

  const removeEmail = (emailToRemove: string) => {
    onEmailsChange(emails.filter((email) => email !== emailToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputValue.trim()) {
        addEmail(inputValue);
      }
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const emailList = pastedText
      .split(/[,\n\s]+/)
      .map((email) => email.trim())
      .filter((email) => email && validateEmail(email));

    const newEmails = [...emails];
    emailList.forEach((email) => {
      if (!newEmails.includes(email)) {
        newEmails.push(email);
      }
    });
    onEmailsChange(newEmails);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg bg-white min-h-[44px]">
        {emails.map((email, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-1 text-sm"
          >
            {email}
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <Input
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={emails.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[200px] border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
      <p className="text-xs text-gray-500">
        Press Enter or comma to add emails. You can also paste multiple emails
        separated by commas.
      </p>
    </div>
  );
};

export default MultiEmailInput;
